import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';

/**
 * Where Stripe Checkout sends the payer back (<public-url>/payment/result?status=…).
 * PUBLIC on purpose: from the phone app, checkout opens in the phone's browser, where
 * the user isn't logged in — the old login-only return page bounced them to the login
 * screen. In that case the plan is activated by the Stripe webhook on the server, and
 * this page just says "return to the app" (the app re-checks the subscription when it
 * comes back to the foreground). Logged in on the website, it confirms right here.
 */
export default function PaymentResult() {
  const [params] = useSearchParams();
  const status = params.get('status');
  const sessionId = params.get('session_id') || '';
  const { user, loading, refreshUser } = useAuth();
  const [state, setState] = useState(status === 'success' ? 'checking' : 'canceled');
  const [error, setError] = useState('');

  useEffect(() => {
    if (status !== 'success' || loading) return;
    if (!user) { setState('paid-guest'); return; }
    api.post(`/payment/confirm-stripe?sessionId=${encodeURIComponent(sessionId)}`)
      .then(() => { setState('paid'); refreshUser(); })
      .catch((e) => {
        setError(e.response?.data?.message || 'Could not confirm the payment right now.');
        setState('error');
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [status, loading, user?.id]);

  const view = {
    checking: { icon: '…', tone: 'bg-slate-100 text-slate-500', title: 'Confirming your payment…', body: 'This takes a few seconds.' },
    paid: { icon: '✓', tone: 'bg-emerald-100 text-emerald-600', title: 'Payment successful', body: 'Your plan is active. Thank you for subscribing!' },
    'paid-guest': {
      icon: '✓', tone: 'bg-emerald-100 text-emerald-600', title: 'Payment received',
      body: 'Return to the StockXignal app — your plan activates automatically in a moment. On the website, just log in.',
    },
    canceled: { icon: '×', tone: 'bg-slate-100 text-slate-500', title: 'Payment canceled', body: 'No charge was made. You can choose a plan again anytime.' },
    error: {
      icon: '!', tone: 'bg-amber-100 text-amber-600', title: 'Almost there',
      body: `${error} If you completed the payment, your plan activates automatically within a few minutes. Otherwise contact us from the Help page.`,
    },
  }[state];

  return (
    <div className="flex min-h-[70vh] items-center justify-center">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-6 text-center shadow-sm">
        <div className={`mx-auto flex h-14 w-14 items-center justify-center rounded-full text-2xl font-bold ${view.tone}`}>
          {view.icon}
        </div>
        <h1 className="mt-4 text-xl font-bold text-slate-900">{view.title}</h1>
        <p className="mt-2 text-sm text-slate-600">{view.body}</p>
        <div className="mt-5 flex flex-col gap-2">
          {user && state === 'paid' && (
            <Link to="/signals" className="rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Go to Signals</Link>
          )}
          {user && (state === 'canceled' || state === 'error') && (
            <Link to="/subscribe" className="rounded-xl bg-slate-900 py-2.5 text-sm font-semibold text-white hover:bg-slate-800">Back to plans</Link>
          )}
          {!user && state !== 'checking' && (
            <Link to="/login" className="rounded-xl bg-slate-100 py-2.5 text-sm font-semibold text-slate-800 hover:bg-slate-200">Log in on the website</Link>
          )}
        </div>
      </div>
    </div>
  );
}
